// Requirement 20: package-specific release notes in the app — «Τι νέο υπάρχει» (Help), the desktop updater popup and
// the web app's update prompt all show the same data file (@vero/platform release-notes.ts), filtered by this device's
// rights: «included in your plan», «available as an upgrade (plan X / add-on Y)» — information only, nothing is switched
// on or charged — or nothing for what everyone gets. Store builds show no plan or purchase wording (PURCHASE_UI).
// The platform serves the notes of its own (newer) release at GET /v1/platform/release-notes, so an installed app can
// describe a version it does not contain yet; without it the bundled file is used.
import { useEffect, useState } from 'react';
import { APP_VERSION } from '@vero/backup';
import { PLAN_NAMES, RELEASE_NOTES, compareVersions, notesForUser, parseReleaseNotes, type NoteGroup, type NoteView, type ReleaseNote } from '@vero/platform';
import { useRuntime } from '../core/runtime.tsx';
import { useRights } from '../core/rights.tsx';
import { PURCHASE_UI } from '../core/edition.ts';
import { lang3 } from '../guide/l3.ts';
import { platformBase } from './maintenance.tsx';

/** the platform's notes (its release may be newer than this app) merged with the bundled ones; ids are unique */
export async function fetchReleaseNotes(base = platformBase(), f: typeof fetch = fetch): Promise<{ version: string | null; notes: ReleaseNote[] }> {
  if (!base || typeof f !== 'function') return { version: null, notes: RELEASE_NOTES };
  try {
    const r = await f(`${base}/v1/platform/release-notes`, { cache: 'no-store' });
    if (!r.ok) return { version: null, notes: RELEASE_NOTES };
    const body = await r.json() as { version?: unknown };
    const served = parseReleaseNotes(body) ?? [];
    const ids = new Set(served.map((n) => n.id));
    return { version: typeof body.version === 'string' ? body.version : null, notes: [...served, ...RELEASE_NOTES.filter((n) => !ids.has(n.id))] };
  } catch { return { version: null, notes: RELEASE_NOTES }; }
}
export function useServedNotes(enabled = true): { version: string | null; notes: ReleaseNote[] } | null {
  const [x, setX] = useState<{ version: string | null; notes: ReleaseNote[] } | null>(null);
  useEffect(() => { if (!enabled) return; let on = true; void fetchReleaseNotes().then((r) => { if (on) setX(r); }); return () => { on = false; }; }, [enabled]);
  return x;
}

function NoteChip({ v }: { v: NoteView }) {
  const { t } = useRuntime();
  if (v.status === 'included') return <span className="chip ok" data-testid="note-included">{t('notes.included')}</span>;
  if (v.status === 'upgrade') return PURCHASE_UI
    ? <span className="chip gold" data-testid="note-upgrade">{t('notes.upgrade', { plans: [...v.plans.map((p) => PLAN_NAMES[p]), ...v.addons].join(', ') })}</span>
    : <span className="chip" data-testid="note-not-in-plan">{t('notes.not_in_plan')}</span>;
  if (v.note.kind === 'security' || v.note.kind === 'fix') return <span className="chip teal">{t(`notes.kind.${v.note.kind}`)}</span>;
  return null;
}

/** grouped notes with their chips; `heading` false for the compact lists of the update prompts */
export function NotesList({ groups, heading = true }: { groups: NoteGroup[]; heading?: boolean }) {
  const rt = useRuntime();
  const l = lang3(rt.locale);
  return <>{groups.map((g) => <div key={g.key} className={heading ? 'card card-pad' : ''} data-testid="notes-group">
    {heading && <div className="row" style={{ gap: 10, alignItems: 'baseline' }}><h2 style={{ margin: 0 }}>{rt.t('help.version', { v: g.key })}</h2><span className="muted small">{rt.i18n.date(g.date, 'long')}</span></div>}
    <ul className="guide-new">{g.items.map((v) => <li key={v.note.id} data-testid="note-item" data-status={v.status}>{v.note.text[l]} <NoteChip v={v} /></li>)}</ul>
  </div>)}</>;
}

/** «Τι νέο υπάρχει»: what this installed version contains, by rights */
export function WhatsNew() {
  const rights = useRights();
  const groups = notesForUser(rights, { upTo: APP_VERSION });
  const { t } = useRuntime();
  return <section className="stack" style={{ gap: 14 }} data-testid="help-whats-new">
    <p className="small muted" style={{ margin: 0 }}>{t(PURCHASE_UI ? 'notes.intro' : 'notes.intro_store')}</p>
    <NotesList groups={groups} />
  </section>;
}

/** the notes between the installed version and an offered one (updater popup, web app update prompt) */
export function UpdateNotes({ target, notes }: { target: string | null; notes: ReleaseNote[] }) {
  const rights = useRights();
  const { t } = useRuntime();
  if (!target || compareVersions(target, APP_VERSION) <= 0) return null;
  const groups = notesForUser(rights, { after: APP_VERSION, upTo: target, notes });
  if (!groups.length) return null;
  return <div data-testid="update-plan-notes">
    <div className="small muted" style={{ marginBottom: 4 }}>{t('notes.in_update')}</div>
    <NotesList groups={groups} heading={false} />
    {groups.some((g) => g.items.some((v) => v.status === 'upgrade')) && <p className="tiny muted" style={{ margin: 0 }}>{t(PURCHASE_UI ? 'notes.upgrade_hint' : 'notes.upgrade_hint_store')}</p>}
  </div>;
}
